import { Dayjs } from 'dayjs';
import { OpportunityTagModel } from './admin-rule-models';
import { QualifyingAnswer } from '../utils/lead-queue-utils';
import { FlowQuestion } from './qualifying-flow-models';

export interface OpportunitySearchResult {
  customerSalutation: string;
  customerFirstName: string;
  customerLastName: string;
  phoneNumber: string;
  email: string;

  internalId: string;
  signupId: string;
  globalCustomerId: string;
  opportunityId: string;
  bookingStatus: string | null;
  planningStatus: string | null;
  fullAddress: string;
  createdDate: Date;
  caseId: string;
  doNotCallBefore: Dayjs | null;

  externalSlotId: string | null;
  appointmentStartDate: Dayjs | null;
  appointmentStatus: string | null;
  salesFirstName: string | null;
  salesLastName: string | null;
  externalAppointmentId: string | undefined;
  cancellationReason: string | null;
  internalComments: string | null;

  assignedAgent: string | null;
  rescheduling: boolean | null;

  salesChannel: SalesChannel | null;
  isAutodialed: boolean;
}

export interface OpportunityAssignDto {
  internalId: string;
  /** True when assignment comes from the autodialer deep-link (/booking?type=autodialer&id=...). */
  isAutodialerFlow?: boolean;
}

export enum LeadTier {
  None = 'None',
  Gold = 'Gold',
  Silver = 'Silver',
  Bronze = 'Bronze',
  Wood = 'Wood',
  ThirdParty = 'ThirdParty',
  Copper = 'Copper',
}

export interface Calendar {
  lead: Lead;
  slots: CalendarSlot[];
}

export interface Lead {
  gcid: string;
  opportunityId: string;
  salutation: string;
  firstName: string;
  lastName: string;
  tier: LeadTier;
  referredById: string | null;
  referredByName: string | null;
  products: string;
}

export interface CalendarSlot {
  startDate: Dayjs;
  dailyAvailableCount: number;
  salesChannel: SalesChannel;
}

export interface Slot {
  id: number;
  startDate: Dayjs;
  endDate: Dayjs;
  salesManagerName: string;
  externalType: string;
  externalStatus: string;
  meetingUrl: string;
  metaBookingInfo: string;
  externalId: string;
}

export interface SalesManagerAvailability {
  id: number;
  name: string;
  dailyBookCount: number;
  salesLeague: number;
}

export enum AppointmentType {
  None = 0,
  Slot = 1,
  SalesCall1 = 2,
  SalesCall2 = 3,
}

interface Pagination {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface Result<T> {
  data: T;
  errors: string[] | null;
  isSuccess: boolean;
}

export interface PaginatedResult<T> extends Result<T> {
  pagination: Pagination;
}

export interface SlotConflict {
  slotId: number;
  slotDate: string;
  conflictingSlotId: number;
  conflictingSlotSubject: string;
  salesManagerId: number;
  salesManagerName: string;
  salesManagerLeague: number;
  salesManagerTeamName: string;
  slotUrl: string;
  slotTier: string;
  bookedOpportunityId: string;
}

export interface CaseInfo {
  internalId: string;
  processingType: string;
  globalCustomerId: string;
  opportunityId: string;
  signupId: string;
  caseId: string;
  referrer: string;
  contactId: string;
  salutation: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  leadPhoneNumber: string ;
  leadPhoneId: string | null;
  street: string;
  houseNumber: string;
  city: string;
  zipcode: string;
  bookingStatus: string;
  leadTier: LeadTier;
  paymentModel: string;
  productType: string;
  caseComments: CaseComments[];
  callCounter: number;
  notReachedCounter: number;
  doNotCallBefore: Date | null;
  salesScript: string;
  salesObjections: OpportunityObjection[];
  salesChannel: SalesChannel | null;
  expireAt: Date | null;
  createdDate: Date | null;
  leadSource: string;
  aBTestingFragment: string;
  opportunityTags: OpportunityTagModel[];
  qualifyingQuestions: FlowQuestion[];
  isFieldSalesEligible: boolean;
  showQualifyingQuestions: boolean;
  isAutodialed: boolean;
  configurableLostReasonsInBooking?: boolean;
}

export const defaultCaseInfo: CaseInfo = {
  internalId: '',
  processingType: '',
  globalCustomerId: '',
  opportunityId: '',
  signupId: '',
  caseId: '',
  referrer: '',
  contactId: '',
  salutation: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  leadPhoneNumber: '',
  leadPhoneId: null,
  street: '',
  houseNumber: '',
  city: '',
  zipcode: '',
  bookingStatus: '',
  leadTier: LeadTier.None,
  productType: '',
  paymentModel: '',
  caseComments: [],
  callCounter: 0,
  notReachedCounter: 0,
  doNotCallBefore: null,
  salesScript: '',
  salesObjections: [],
  salesChannel: null,
  expireAt: null,
  createdDate: null,
  leadSource: '',
  aBTestingFragment: '',
  opportunityTags: [{ feId: crypto.randomUUID(), color: '', value: '' }],
  qualifyingQuestions: [],
  isFieldSalesEligible: false,
  showQualifyingQuestions: false,
  isAutodialed: false,
  configurableLostReasonsInBooking: false,
};

export interface CaseComments {
  body: string;
  createdBy: string;
  createdOn: Date;
  isCustomerLostReason: boolean;
  looseReasons: string;
  isNotBookedReason: boolean;
}

export interface OpportunityComplete {
  id: string;
  opportunityId: string;
  globalCustomerId: string;
  caseId: string;
  signUpId: string;
  opportunityComments: string;
  doNotCallBefore: Date | null;
  customerLostReasons: string[] | null;
  customerLostDescription: string | null;
  caseStatus: CaseStatus;
  shareContactData?: boolean | null;
  isDataDeletionRequested: boolean;
  qualifyingAnswers?: QualifyingAnswer[];
  notBookedReason?: string;
  wrongContactId?: string;
}

export interface CaseAssignment {
  caseId: string;
  userEmail: string;
  expireAt: Date | null;
}

export interface TotalOpportunities {
  count: number;
  opportunities: CaseInfo[];
}

export enum CaseStatus {
  None = 0,
  Booked = 1,
  ReachedButNotBooked = 2,
  NotReached = 3,
  CustomerLost = 4,
  WrongNumber = 5,
}

export interface BulkUploadCase {
  cases: string[];
  overrides: {
    callCount: string | undefined;
  };
}

export enum SalesChannel {
  MissingQueue = 'MissingQueue',
  DigitalSales = 'DigitalSales',
  SalesOS = 'SalesOS',
  CrmSales = 'CrmSales',
  Revision = 'Revision',
  SalesCall2 = 'SalesCallTwo',
  FieldSales = 'FieldSales',
  LeadQueue = 'LeadQueue',
  Retention = 'Retention',
  SpecialSalesChannel1 = 'SpecialSalesChannel1',
  SpecialSalesChannel2 = 'SpecialSalesChannel2',
  SpecialSalesChannel3 = 'SpecialSalesChannel3',
  SpecialSalesChannel4 = 'SpecialSalesChannel4',
  SpecialSalesChannel5 = 'SpecialSalesChannel5'
}

export enum ProductType {
  UNKNOWN = 'UNKNOWN',
  PV = 'PV',
  ZEUS = 'ZEUS',
  COMBINED = 'COMBINED',
  HEATPUMP = 'HEATPUMP',
}

export enum PaymentModel {
  ANY = 'Any',
  LEASE = 'Lease',
  LOAN = 'Loan',
}

export enum Department {
  NONE = 'NONE',
  CustomerService = 'Customer Service',
  IDNow = 'IDNow',
  HomeImprovement = 'Home Improvement',
  SalesBerlin = 'Sales Berlin',
  SalesEssen = 'Sales Essen',
  SalesFrankfurt = 'Sales Frankfurt',
  SalesHamburg = 'Sales Hamburg',
  SalesLeipzig = 'Sales Leipzig',
  SalesMunchen = 'Sales München',
  SalesRetention = 'Sales Retention',
}

export enum SalesLeague {
  Zero = 0,
  One = 1,
  Two = 2,
  Three = 3,
  Four = 4,
  Five = 5,
  Six = 6,
  Seven = 7,
  Eight = 8,
  Nine = 9,
  Ten = 10,
  Eleven = 11,
  Twelve = 12,
  Thirteen = 13,
  Fourteen = 14,
}

export interface OpportunityEditContactDto {
  opportunityId: string;
  signUpId: string;
  globalCustomerId: string;
  contactId: string;
  firstName: string;
  oldFirstName?: string;
  lastName: string;
  oldLastName?: string;
  email: string;
  oldEmail?: string;
  phone: string;
  salutation: string;
  oldSalutation?: string;
  street: string;
  oldStreet?: string;
  houseNumber: string;
  oldHouseNumber?: string;
  city: string;
  oldCity?: string;
  zipCode: string;
  oldZipCode?: string;
}

export interface CustomerDeleteData {
  customerFullName: string;
  bookingAgentFullName: string;
  contactUrl: string;
}

export interface SalesManager {
  id: number;
  lastModifiedDate: Date | null;
  lastModifiedBy: string;
  isDeleted: boolean;
  firstName: string;
  lastName: string;
  salesLeague: SalesLeague;
  productType: ProductType;
  externalAccountId: string;
  email: string;
  teamName: string;
  isTeamLead: boolean;
  paymentModel: PaymentModel;
  azureId: string;
  regions: string[];
  salesChannel: SalesChannel;
  department: Department;
}

export interface GroupSlotData {
  externalAppointmentId: string;
  appointmentTypeId: number;
  startDateTimeUtc: Date;
  productType: string;
  paymentModel: string;
  salesManagerCount: number;
  isOverbookSlot: boolean;
}

export interface BaseCardModel {
  name: string;
  description: string;
}

export interface ScriptsWithObjections extends BaseCardModel {
  name: string;
  scripts: OpportunityScript[];
  objections: OpportunityObjection[];
}

export interface OpportunityScript {
  id: number;
  name: string;
  bookingStatus: string;
  scriptText: string;
  lastModifiedDate: Date;
  lastModifiedBy: string;
  isDeleted: boolean;
}

export interface OpportunityObjection {
  id: number;
  scriptName: string;
  objection: string;
  answer: string;
  sortOrder: number;
  lastModifiedDate: Date;
  lastModifiedBy: string;
  isDeleted: boolean;
}

export type CommonEditComponentProps<T> = {
  model: T;
  onSubmit: (form: T) => void;
  onCancel: () => void;
};

export enum BookingStatus {
  FirstTimeScheduling = 'First Time Scheduling',
  ReschedulingNeeded = 'Rescheduling Needed',
}

export type UserCustomizableUI = {
  selectedBookingButton: string | undefined;
};

export interface EditContact {
  id: number;
  opportunityId: string;
  contactId: string;
  firstName: string;
  oldFirstName?: string | null;
  lastName: string;
  email: string;
  oldEmail?: string | null;
  oldLastName?: string | null;
  city?: string | null;
  oldCity?: string | null;
  street?: string | null;
  oldStreet?: string | null;
  houseNumber?: string | null;
  oldHouseNumber?: string | null;
  zipCode?: string | null;
  oldZipCode?: string | null;
  salutation?: string | null;
  oldSalutation?: string | null;
  contactLink: string;
  opportunityLink: string;
  isCompleted: boolean;
  createdAtUtc: Date;
  modifiedAtUtc: Date;
}

export interface StartCallResponseBody {
  callId: string;
}

export interface CallBodyRequest {
  customerPhoneNumber: string;
  customerId: string;
}
